<template>
  <div v-if="hasAssets || hasFencedSection" class="py-6">
    <div class="flex items-start justify-between">
      <LabelValue class="pr-8" :label="t('Reviews.agency.view.assets.title')">
        <template #hint>
          {{ t('Reviews.agency.view.assets.description') }}
        </template>
      </LabelValue>
      <OpenAgencyProfile class="shrink-0" :slug="agency.slug" />
    </div>
    <SponsoredContentBanner class="mb-8" data-testid="sponsored-content-banner" />
    <LabelValue :label="t('Reviews.agency.view.assets.images.label').toString()" data-testid="agency-screenshots" :show-icon="hasImages">
      <template #hint>
        {{ hasImages ? t('Reviews.agency.view.assets.images.hint') : t('Reviews.agency.view.assets.images.missing') }}
      </template>
      <div v-if="screenshotsFenced">
        {{ t('Reviews.agency.view.screenshots.notActive') }}
      </div>
      <div v-else-if="hasImages" class="grid grid-cols-3 gap-6">
        <AssetPreview
          v-for="screenshot in agency.screenshots"
          :key="screenshot.url"
          :content-type="screenshot.contentType"
          :name="screenshot.name"
          :url="screenshot.url"
          data-testid="image-preview"
        />
      </div>
    </LabelValue>
    <LabelValue
      :label="t('Reviews.agency.view.assets.documents.label').toString()"
      data-testid="agency-documents"
      :show-icon="hasDocuments"
    >
      <template #hint>
        {{ hasDocuments ? t('Reviews.agency.view.assets.documents.hint') : t('Reviews.agency.view.assets.documents.missing') }}
      </template>
      <div v-if="documentsFenced">
        {{ t('Reviews.agency.view.documents.notActive') }}
      </div>
      <div v-else-if="hasDocuments" class="grid grid-cols-4 gap-6">
        <DocumentCard
          v-for="doc in documents"
          :key="doc.url"
          :content-type="doc.contentType"
          :name="doc.name"
          :size="doc.size"
          :preview-url="doc.previewUrl || undefined"
          data-testid="document-card"
          @click="downloadFile(doc.url)"
        />
      </div>
    </LabelValue>
  </div>
  <ReviewsEmptyView
    v-else
    class="py-8"
    data-testid="agency-no-assets-view"
    :title="t('Reviews.agency.view.assets.noAssetsTitle')"
    :description="t('Reviews.agency.view.assets.noAssetsInfo')"
    :action="{
      label: t('Reviews.agency.view.actions.addAssets'),
      to: {
        name: ReviewsRouteName.ReviewsAgencyEditAssets,
        params: { agencyId: agency.id },
      },
    }"
  >
    <template #image>
      <div class="rounded-full bg-surface-elevation-5 p-3">
        <NoAssetsIcon />
      </div>
    </template>
  </ReviewsEmptyView>
</template>

<script setup lang="ts">
import { DocumentCard } from '@ramp106/omrjs-reviews-ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import OpenAgencyProfile from '@/components/ReviewsAgenciesManagements/OpenAgencyProfile.vue'
import NoAssetsIcon from '@/components/ReviewsCommon/ReviewsAssetsEdit/NoAssetsIcon.vue'
import ReviewsEmptyView from '@/components/ReviewsEmptyView.vue'
import SponsoredContentBanner from '@/components/ReviewsManagement/SponsoredContentBanner.vue'
import { type AgencyData } from '@/gql/reviews'
import { ALLOWED_LOCALES, mapTranslationsArrToObj } from '@/helpers/translationsHelper.ts'
import { ReviewsRouteName } from '@/router/types.ts'
import AssetPreview from '@/ui/AssetPreview.vue'
import LabelValue from '@/ui/LabelValue.vue'

const props = defineProps<{
  agency: AgencyData
}>()

const { t, locale } = useI18n()

const hasImages = computed(() => props.agency.screenshots.length > 0)
const hasDocuments = computed(() => props.agency.documents.length > 0)
const hasAssets = computed(() => hasImages.value || hasDocuments.value)
const documentsFenced = computed(() => !props.agency.configFlags.documentsEditable)
const screenshotsFenced = computed(() => !props.agency.configFlags.screenshotsEditable)
const hasFencedSection = computed(() => documentsFenced.value || screenshotsFenced.value)

const documents = computed(() =>
  props.agency.documents.map((doc) => {
    const nameTranslationsMap = mapTranslationsArrToObj(doc.nameTranslations)
    const name = nameTranslationsMap[locale.value] || nameTranslationsMap[ALLOWED_LOCALES[0]]
    return { ...doc, name }
  }),
)

function downloadFile(url: string) {
  window.open(url, '_blank')
}
</script>
